import React, { useCallback } from "react";

interface InteractiveSliderProps {
  value?: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
}

export const Slider: React.FC<InteractiveSliderProps> = ({
  value,
  onChange,
  min = 1,
  max = 7,
  step = 1,
  className = "",
}) => {
  // Use fractional positions for clean relative positioning
  const totalPositions = max - min; // 0 to 6 = 7 positions
  if (!value || value < min || value > max) {
    value = Math.floor((min + max) / 2);
  }
  
  // Chart bars use 94% width of container, so we need to adjust slider positions
  // to align with the chart bars within that 94% space
  const chartBarLeftPercent = ((value - min) / totalPositions) * 100;
  
  // Slider track is full width (100%), but chart bars are in 94% width
  // We need to map the chart bar positions to the full slider track width
  const chartContainerWidthPercent = 94; // Chart bars container is 94% of parent
  const chartContainerOffset = (100 - chartContainerWidthPercent) / 2; // 3% offset on each side
  
  // Map chart bar position to slider track position and center the slider handle
  // Chart bars are 8px wide (w-2), slider handle is 16px wide (w-4)
  // We need to account for the difference in centering
  const barWidth = 8; // Chart bar width in pixels
  const sliderHandleWidth = 16; // Slider handle width in pixels
  const trackWidth = 547; // Total slider track width in pixels
  
  // Convert the centering offset to percentage
  const centeringOffsetPx = (barWidth - sliderHandleWidth) / 2; // -4px to center handle on bar
  const centeringOffsetPercent = (centeringOffsetPx / trackWidth) * 100;
  
  const sliderLeftPercent = chartContainerOffset + (chartBarLeftPercent * chartContainerWidthPercent / 100) + centeringOffsetPercent;

  const handleSliderChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(parseFloat(event.target.value));
  }, [onChange]);

  return (
    <div className={`flex flex-col items-start gap-4 relative self-stretch w-full flex-[0_0_auto] ${className}`}>
      <div className="relative self-stretch w-full h-10">
        {/* Background track */}
        <div className="absolute top-[calc(50.00%_-_4px)] left-0 w-full h-2 bg-foundation-violetviolet-600 rounded-[48px]" />
        
        {/* Orange progress track */}
        <div 
          className="absolute top-[calc(50.00%_-_4px)] left-0 h-2 bg-foundation-orange-500 rounded-[48px] transition-all duration-300"
          style={{ width: `${sliderLeftPercent + 1.5}%` }}
        />
        
        {/* Slider glow effect */}
        <div 
          className="absolute top-[calc(50.00%_-_20px)] w-[2.125rem] h-10 bg-foundation-orange-500/10 rounded-[17.1px/20px] blur-sm transition-all duration-300"
          style={{ left: `calc(${sliderLeftPercent}% - 9px)` }}
        />
        
        {/* Slider handle */}
        <div 
          className="absolute top-[calc(50.00%_-_8px)] w-4 h-4 bg-foundation-orange-500 rounded-lg border border-solid border-white transition-all duration-300 cursor-pointer"
          style={{ left: `${sliderLeftPercent}%` }}
        />
        
        {/* Invisible input for interaction */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleSliderChange}
          className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
        />
      </div>
    </div>
  );
};
